import { peekFitnessGuidance } from '@/i18n/translations';
import { getExplicitFitnessMovement } from '@/lib/fitnessExerciseIdentity';
import { detectLangFromTags, normalizeLang } from '@/i18n/locale';

/** Original static instructions for exact selected machine variants.
 * Source principles and declared product adaptations:
 * docs/research/fitness-rea-20261015/guidance-drafts/upper-machine12/sources.json.
 * Exact high-to-low Crossover evidence: docs/qa/fitness-completion-20261015/dual-cable-two-original-v15/guidance-proposal/sources-and-contract.json.
 * Its corrected canonical app identity follows the separate real-identity-correction.json erratum.
 * Static guidance does not admit media or infer observation/professional assessment.
 */
export const MACHINE_EXERCISE_GUIDANCE_IDS = [
  "seated-forearm-pad-pec-deck",
  "seated-machine-chest-press",
  "flat-two-hand-dumbbell-pullover",
  "seated-front-lat-pulldown",
  "seated-neutral-cable-row",
  "landmine-neutral-t-bar-row",
  "seated-neutral-close-grip-pulldown",
  "standing-chest-height-cable-fly",
  "standing-bilateral-cable-row",
  "bent-over-bilateral-dumbbell-row",
  "weighted-strict-pronated-pull-up",
  "standing-high-cable-crossover"
] as const;
export type MachineExerciseGuidanceId = typeof MACHINE_EXERCISE_GUIDANCE_IDS[number];
type GuidanceCopy = readonly [string, string, string, string, string];
export type MachineExerciseGuidanceDictionary = Record<MachineExerciseGuidanceId, GuidanceCopy>;
export const MACHINE_GUIDANCE_MOVEMENT_BINDINGS = [
  [
    "seated-forearm-pad-pec-deck",
    "seated-forearm-pad-pec-deck"
  ],
  [
    "seated-machine-chest-press",
    "seated-machine-chest-press"
  ],
  [
    "flat-two-hand-dumbbell-pullover",
    "flat-two-hand-dumbbell-pullover"
  ],
  [
    "seated-front-lat-pulldown",
    "seated-front-lat-pulldown"
  ],
  [
    "seated-neutral-cable-row",
    "seated-neutral-cable-row"
  ],
  [
    "landmine-neutral-t-bar-row",
    "landmine-neutral-t-bar-row"
  ],
  [
    "seated-neutral-close-grip-pulldown",
    "seated-neutral-close-grip-pulldown"
  ],
  [
    "standing-chest-height-cable-fly",
    "standing-chest-height-cable-fly"
  ],
  [
    "standing-bilateral-cable-row",
    "standing-bilateral-cable-row"
  ],
  [
    "bent-over-bilateral-dumbbell-row",
    "bent-over-bilateral-dumbbell-row"
  ],
  [
    "plan-weighted-pull-up",
    "weighted-strict-pronated-pull-up"
  ],
  [
    "standing-high-cable-crossover",
    "standing-high-cable-crossover"
  ]
] as const;
const byMovement = new Map<string, MachineExerciseGuidanceId>(MACHINE_GUIDANCE_MOVEMENT_BINDINGS);

/** Exact declared identity only; display, provider and approximate variants are not inferred. */
export function getMachineExerciseGuidance(exerciseName: string, lang?: string): { steps: string[]; cues: string[] } | null {
  const id = getExplicitFitnessMovement(exerciseName)?.id;
  const key = id ? byMovement.get(id) : undefined;
  if (!key) return null;
  const language = normalizeLang(lang) ?? detectLangFromTags(lang ? [lang] : []) ?? 'EN';
  const copy = peekFitnessGuidance(language, 'machine', key);
  if (!copy) return null;
  return { steps: copy.slice(0, 3), cues: copy.slice(3) };
}
